import { closureLabel, type CoachLesson } from './use-coach-day.js';

/**
 * A coach's day is hours, and an hour holds one lesson record per athlete.
 *
 * Since migration 012 every child has their own record, so two siblings — or
 * two families sharing a group — arrive as separate lessons with the same
 * `slot_id`. The day list shows the hour once; the lesson screen offers its
 * athletes as a choice of whose record to write on.
 */
export interface CoachHour {
  slotId: string;
  /** In the API's order: by start, then by athlete name. Never empty. */
  lessons: [CoachLesson, ...CoachLesson[]];
}

export function hoursOf(lessons: CoachLesson[]): CoachHour[] {
  const hours: CoachHour[] = [];
  const bySlot = new Map<string, CoachHour>();
  for (const lesson of lessons) {
    const hour = bySlot.get(lesson.slot_id);
    if (hour) {
      hour.lessons.push(lesson);
      continue;
    }
    const first: CoachHour = { slotId: lesson.slot_id, lessons: [lesson] };
    bySlot.set(lesson.slot_id, first);
    hours.push(first);
  }
  return hours;
}

/**
 * Athletes still in the hour. A cancelled lesson stays on the day as a record
 * (ruled 10 October) but its athlete is not coming.
 */
export function stillComing(hour: CoachHour): CoachLesson[] {
  return hour.lessons.filter((lesson) => lesson.cancellation === null);
}

/**
 * Which record a tap on the hour opens: the first athlete still open, because
 * that is the one with work left on it. Then anyone still coming, then the
 * first — an hour where everyone cancelled still opens, to show who did.
 */
export function lessonToOpen(hour: CoachHour): CoachLesson {
  const coming = stillComing(hour);
  return coming.find((lesson) => lesson.closure === null) ?? coming[0] ?? hour.lessons[0];
}

/**
 * What the hour's row says happened. Nothing until every athlete still coming
 * is closed — an hour half written up is not finished. When they all ended the
 * same way it says how; when they did not, it says only that they are closed,
 * and the lesson screen has each one. An hour everyone cancelled says so.
 */
export function hourOutcome(hour: CoachHour): { label: string; creditUsed: boolean } | null {
  const coming = stillComing(hour);
  if (coming.length === 0) return { label: 'Cancelled', creditUsed: false };

  const labels = coming.map(closureLabel);
  const closed = labels.filter((label): label is string => label !== null);
  if (closed.length < labels.length) return null;
  const same = closed.every((label) => label === closed[0]);
  return {
    label: same && closed[0] ? closed[0] : 'Closed',
    creditUsed: coming.every((lesson) => lesson.closure?.creditUsed === true),
  };
}
